// Repositories cloned into the group's workspace besides a GitHub source's own (S56): rows of
// `owner/repo` and an optional branch, folded away until wanted. When the workflow works on
// GitHub and the source carries no repository, say that the agent would have no code.
import { GitBranch, Plus, Trash, Warning } from "@phosphor-icons/react";
import { ActionIcon, Button, Input } from "rizzui";

import { useT } from "../../../i18n";
import { MAX_REPOSITORIES, type RepositoryProblem, type RepositoryRow } from "../repositories";

export function RepositoriesEditor({
  rows,
  onChange,
  problem,
  advise,
}: {
  rows: RepositoryRow[];
  onChange: (rows: RepositoryRow[]) => void;
  problem: RepositoryProblem | null;
  /** The workflow needs GitHub and nothing gives the agent a repository. */
  advise: boolean;
}) {
  const t = useT().tasks.create.repositories;
  const set = (i: number, patch: Partial<RepositoryRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, { repo: "", branch: "" }]);
  const rowError = (i: number) => (problem && problem.kind !== "tooMany" && problem.row === i ? t.problems[problem.kind] : undefined);
  return (
    <section aria-label={t.title} className="rounded-xl border border-muted p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <GitBranch className="h-4 w-4 text-gray-500" aria-hidden /> {t.title}
          </h3>
          <p className="mb-0 mt-0.5 text-[13px] text-gray-500">{t.description}</p>
        </div>
        {rows.length < MAX_REPOSITORIES ? (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={add}>
            <Plus weight="bold" className="h-3.5 w-3.5" aria-hidden /> {t.add}
          </Button>
        ) : null}
      </div>
      {advise && rows.every((r) => r.repo.trim() === "") ? (
        <p className="mb-0 mt-3 flex items-start gap-1.5 rounded-lg bg-orange-lighter/50 p-2.5 text-[13px] text-gray-700">
          <Warning weight="fill" className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden /> {t.advice}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="m-0 mt-3 list-none space-y-3 p-0">
          {rows.map((r, i) => (
            <li key={i} className="grid items-start gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,12rem)_auto]">
              <Input aria-label={t.repo(i + 1)} placeholder="acme/payments" value={r.repo} error={rowError(i)} onChange={(e) => set(i, { repo: e.target.value })} />
              <Input aria-label={t.branch(i + 1)} placeholder={t.branchPlaceholder} value={r.branch} onChange={(e) => set(i, { branch: e.target.value })} />
              <ActionIcon variant="text" aria-label={t.remove(i + 1)} onClick={() => onChange(rows.filter((_, j) => j !== i))} className="mt-1">
                <Trash className="h-4 w-4" />
              </ActionIcon>
            </li>
          ))}
        </ul>
      ) : null}
      {problem?.kind === "tooMany" ? <p className="mb-0 mt-2 text-[13px] text-red">{t.problems.tooMany}</p> : null}
    </section>
  );
}
